import type { CompanyStatus } from '../lib/types.ts';
import { companyStatusLabel } from '../lib/companyStatus.ts';
import { docDisplayStatusLabel, type DocDisplayStatus } from '../lib/submissionStatus.ts';
import {
  questionDisplayStatusLabel,
  type QuestionDisplayStatus,
} from '../lib/questionDisplayStatus.ts';

const COMPANY_CLS: Record<CompanyStatus, string> = {
  invited: 'bg-slate-100 text-slate-700',
  in_progress: 'bg-blue-50 text-blue-700',
  review: 'bg-amber-50 text-amber-700',
  changes: 'bg-orange-50 text-orange-700',
  compliant: 'bg-emerald-50 text-emerald-700',
  expired: 'bg-red-50 text-red-700',
  deactivated: 'bg-slate-200 text-slate-600',
};

const SUBMISSION_CLS: Record<DocDisplayStatus, string> = {
  pending: 'bg-slate-100 text-slate-700',
  approved: 'bg-emerald-50 text-emerald-700',
  rejected: 'bg-orange-50 text-orange-700',
  not_applicable: 'bg-slate-100 text-slate-600',
  expired: 'bg-red-50 text-red-700',
};

export function CompanyStatusBadge({
  status,
  audience = 'admin',
}: {
  status: CompanyStatus;
  /** 'vendor' shows the party-being-reviewed wording (e.g. review -> "Under Review by Admin"). */
  audience?: 'admin' | 'vendor';
}) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${COMPANY_CLS[status]}`}
    >
      {companyStatusLabel(status, audience)}
    </span>
  );
}

export function SubmissionStatusBadge({ status }: { status: DocDisplayStatus }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${SUBMISSION_CLS[status]}`}
    >
      {docDisplayStatusLabel(status)}
    </span>
  );
}

// Always-on per-question status for the vendor post-submit view — the four
// review states plus "Not answered" (amber, so a stray unanswered item stands
// out). Reuses the review colours from SUBMISSION_CLS.
const QUESTION_CLS: Record<QuestionDisplayStatus, string> = {
  pending: 'bg-slate-100 text-slate-700',
  approved: 'bg-emerald-50 text-emerald-700',
  rejected: 'bg-orange-50 text-orange-700',
  not_applicable: 'bg-slate-100 text-slate-600',
  not_answered: 'bg-amber-50 text-amber-700',
  expired: 'bg-red-50 text-red-700',
};

export function QuestionStatusBadge({ status }: { status: QuestionDisplayStatus }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${QUESTION_CLS[status]}`}
    >
      {questionDisplayStatusLabel(status)}
    </span>
  );
}
